import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';

const RUNBOOK_URL =
	'https://hello.atlassian.net/wiki/spaces/~7120202da6a0b2c4184c0da3ba8ade6d08feaa/pages/5761728624/React+suspense+boundary+errors+run-book';

type QueryBoundaryErrorMessageProps = {
	operationName: string;
	expectedBoundaries: string[];
	currentBoundaries: string[];
	errorType: 'outside-boundary' | 'boundary-mismatch';
};

export const i18n = defineMessages({
	outsideBoundaryReason: {
		id: 'streaming-boundary-error-notification.boundary-error.outside.reason',
		defaultMessage:
			'Query "{operationName}" preloaded in [{expectedBoundaries}] but accessed outside any suspense boundary. See <runbookLink>this page</runbookLink> for details.',
		description: 'Reason shown when a query is used outside of any suspense boundary.',
	},
	boundaryMismatchReason: {
		id: 'streaming-boundary-error-notification.boundary-error.mismatch.reason',
		defaultMessage:
			'Query "{operationName}" preloaded in [{expectedBoundaries}] but accessed in "{currentBoundaries}" boundary. See <runbookLink>this page</runbookLink> for details.',
		description: 'Reason shown when a query is used in the wrong suspense boundary.',
	},
});

export const QueryBoundaryErrorMessage: React.FC<QueryBoundaryErrorMessageProps> = ({
	operationName,
	expectedBoundaries,
	currentBoundaries,
	errorType,
}: QueryBoundaryErrorMessageProps) => {
	const expectedBoundariesStr = expectedBoundaries.join(', ');
	const currentBoundariesStr = currentBoundaries.join(' → ');

	return (
		<div data-testid="queryBoundaryErrorMessage">
			<Text>
				{errorType === 'outside-boundary' ? (
					<FormattedMessage
						{...i18n.outsideBoundaryReason}
						values={{
							operationName,
							expectedBoundaries: expectedBoundariesStr,
							runbookLink: (chunks: React.ReactNode[]) => (
								<Link target="_blank" href={RUNBOOK_URL}>
									{chunks}
								</Link>
							),
						}}
					/>
				) : (
					<FormattedMessage
						{...i18n.boundaryMismatchReason}
						values={{
							operationName,
							expectedBoundaries: expectedBoundariesStr,
							currentBoundaries: currentBoundariesStr,
							runbookLink: (chunks: React.ReactNode[]) => (
								<Link target="_blank" href={RUNBOOK_URL}>
									{chunks}
								</Link>
							),
						}}
					/>
				)}
			</Text>
		</div>
	);
};
